import { gql } from '@apollo/client';

/**
 * 运维工具 → 揽收：强制合并站点 / 确认无法建联。
 * 均直调 api-server 现有接口；后端鉴权：总部员工权限 或 OPS_TOOL_PICKUP 资源码。
 */

// ---- 强制合并站点 ----

export const FORCE_MERGE_COOPERATION_WAREHOUSES = gql`
  mutation OpsToolForceMergeCooperationWarehouses($targetAddressId: Long!, $sourceAddressIds: [Long!]!) {
    forceMergeCooperationWarehouses(targetAddressId: $targetAddressId, sourceAddressIds: $sourceAddressIds) {
      success
      message
    }
  }
`;

export interface ForceMergeResponse {
  forceMergeCooperationWarehouses: {
    success: boolean;
    message?: string | null;
  };
}

export interface ForceMergeVariables {
  targetAddressId: number;
  sourceAddressIds: number[];
}

/** 合并前预览：只取核对所需字段，不取联系人（避免在运维页面展示手机号） */
export const OPS_TOOL_STATION_PREVIEW = gql`
  query OpsToolStationPreview($id: Long!) {
    getCooperationWarehouseById(id: $id) {
      id
      name
      warehouseId
      warehouseName
      status
      linkStatus
      addressLine1
      addressLine2
      city
      state
      zipcode
    }
  }
`;

export interface StationPreview {
  id: number;
  name: string;
  warehouseId: number;
  warehouseName?: string | null;
  status: number;
  linkStatus?: number | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  zipcode?: string | null;
}

export interface StationPreviewResponse {
  getCooperationWarehouseById: StationPreview | null;
}

// ---- 确认无法建联 / 回滚 ----

const MARK_STATIONS_UNREACHABLE_REPORT_FIELDS = `
  dryRun
  hubId
  batchNo
  requested
  duplicatedInput
  notFoundOrOtherHub
  notFoundOrOtherHubSample
  retiredSkipped
  alreadyUnreachable
  linkStatusUpdated
  skippedByStateDrift
  operationLogWritten
  poolMatched
  poolMarked
  poolSkippedPartial
  poolSkippedDeleted
  poolStuckLinksRetained
  appointmentTargets
  appointmentSuppressed
  appointmentExcludedByStatus
  linkStatusRestored
  poolRestored
  appointmentRestored
  summary
`;

export const MARK_STATIONS_UNREACHABLE = gql`
  mutation OpsToolMarkStationsUnreachable(
    $hubId: Long!
    $cooperationWarehouseIds: [Long!]!
    $dryRun: Boolean!
    $suppressAppointments: Boolean!
  ) {
    markStationsUnreachable(
      hubId: $hubId
      cooperationWarehouseIds: $cooperationWarehouseIds
      dryRun: $dryRun
      suppressAppointments: $suppressAppointments
    ) {
      ${MARK_STATIONS_UNREACHABLE_REPORT_FIELDS}
    }
  }
`;

export const ROLLBACK_MARK_STATIONS_UNREACHABLE = gql`
  mutation OpsToolRollbackMarkStationsUnreachable($batchNo: String!, $dryRun: Boolean!) {
    rollbackMarkStationsUnreachable(batchNo: $batchNo, dryRun: $dryRun) {
      ${MARK_STATIONS_UNREACHABLE_REPORT_FIELDS}
    }
  }
`;

export interface MarkStationsUnreachableReport {
  dryRun: boolean;
  hubId?: number | null;
  batchNo?: string | null;
  requested: number;
  duplicatedInput: number;
  notFoundOrOtherHub: number;
  notFoundOrOtherHubSample: number[];
  retiredSkipped: number;
  alreadyUnreachable: number;
  linkStatusUpdated: number;
  skippedByStateDrift: number;
  operationLogWritten: number;
  poolMatched: number;
  poolMarked: number;
  poolSkippedPartial: number;
  poolSkippedDeleted: number;
  poolStuckLinksRetained: number;
  appointmentTargets: number;
  appointmentSuppressed: number;
  appointmentExcludedByStatus: string[];
  linkStatusRestored: number;
  poolRestored: number;
  appointmentRestored: number;
  summary: string;
}

export interface MarkStationsUnreachableResponse {
  markStationsUnreachable: MarkStationsUnreachableReport;
}

export interface MarkStationsUnreachableVariables {
  hubId: number;
  cooperationWarehouseIds: number[];
  dryRun: boolean;
  suppressAppointments: boolean;
}

export interface RollbackMarkStationsUnreachableResponse {
  rollbackMarkStationsUnreachable: MarkStationsUnreachableReport;
}

export interface RollbackMarkStationsUnreachableVariables {
  batchNo: string;
  dryRun: boolean;
}

/** 解析输入框里的站点 id：逗号 / 换行 / 空格 / 中文逗号分隔；返回去重后的 id、重复数、非法片段 */
export function parseIdList(text: string | undefined): { ids: number[]; duplicated: number; invalid: string[] } {
  const tokens = (text ?? '').split(/[\s,，;；]+/).map((s) => s.trim()).filter(Boolean);
  const seen = new Set<number>();
  const invalid: string[] = [];
  let duplicated = 0;
  tokens.forEach((token) => {
    if (!/^\d+$/.test(token)) {
      invalid.push(token);
      return;
    }
    const id = Number(token);
    if (seen.has(id)) {
      duplicated += 1;
    } else {
      seen.add(id);
    }
  });
  return { ids: Array.from(seen), duplicated, invalid };
}
